import React from 'react';
import { Link } from '@carbon/react';
import type { ColumnHelper } from '@tanstack/react-table';
import type { DataTableConfig } from 'components/data-table/types';
import type { TractNRI } from 'components/nri-table';
import { tagsTextColumn } from 'lib/asset-aliases';

/**
 * Display fallback shared across all summary cells when a value is
 * missing. Em-dash matches `DataTable`'s built-in nil-fallback for
 * `meta.type`-driven cells (`components/data-table/utils.ts`).
 */
export const EMPTY_VALUE = '—';

/**
 * Decimal places for NRI / WI cells. Matches the legacy
 * `formatDecimalFull` output (`Intl.NumberFormat('en-US')` default
 * `maximumFractionDigits: 3`).
 */
export const NRI_PRECISION = 8;

/**
 * Decimal places for acreage cells (Tract Net / Gross Acres). Matches
 * the legacy `tract-details.tsx` precedent of up-to-8-decimal acres.
 */
export const ACRES_PRECISION = 8;

/**
 * `DataTableConfig` preset for the Summary tables — every interactive
 * feature off because the Summary view is read-only per spec
 * (no expand / sort / filter / search / column visibility / reorder /
 * pin / resize). Pass to `<DataTable config>` in both the slots and
 * slot-allocations tables.
 */
export const STATIC_TABLE_CONFIG: DataTableConfig = {
  sorting: false,
  columnFilters: false,
  globalSearch: false,
  columnVisibility: false,
  columnReordering: false,
  columnPinning: false,
  columnResizing: false,
};

/**
 * Row shape consumed by the Summary tables. Stamps a stable `__key`
 * field (read by `<DataTable getRowId>`, which is single-arg in the
 * project's typing) and widens to satisfy DataTable's
 * `TData extends Record<string, unknown>` constraint.
 */
export type DataTableRow<T> = T & { __key: string } & Record<string, unknown>;

/**
 * Wraps `text` in a Carbon `<Link>` opening in a new tab when `href` is
 * present; renders plain text when href is missing, or {@link EMPTY_VALUE}
 * when text itself is missing. Used by the Summary tables for Insights-page
 * cross-links.
 *
 * @param {string | undefined} text - Display text
 * @param {string | undefined} href - Optional link target
 * @return {React.ReactNode} Linked or plain text
 */
export const renderLink = (
  text: string | undefined,
  href: string | undefined
): React.ReactNode => {
  if (!text) return EMPTY_VALUE;
  if (!href) return text;
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {text}
    </Link>
  );
};

/**
 * Decimal places for the Related Packages "Total Acreage" cell. Fixed at 2
 * to match the packages list page, which the section mirrors — unlike the
 * up-to-8-decimal {@link ACRES_PRECISION} used by the tract's own acreage.
 */
export const ACREAGE_DISPLAY_PRECISION = 2;

/**
 * The slot id, the unit and the unit's tags, the three columns the tract page's slot tables share. The tags
 * are comma text, blank with none (INS-13689), in an accessor column so the table's search finds a row by one.
 *
 * @param {ColumnHelper} columnHelper - The table's column helper, over its keyed rows.
 * @param {string} slotIdHeader - What the table calls the slot id column.
 * @return {Array} The three column definitions.
 */
export const slotUnitColumns = (
  columnHelper: ColumnHelper<DataTableRow<TractNRI>>,
  slotIdHeader: string
) => [
  columnHelper.display({
    id: 'slotId',
    header: slotIdHeader,
    cell: ({ row }) => renderLink(row.original.slotId, row.original.slotIdHref),
  }),
  columnHelper.display({
    id: 'unit',
    header: 'Unit',
    cell: ({ row }) => renderLink(row.original.unit, row.original.unitHref),
  }),
  tagsTextColumn<DataTableRow<TractNRI>>({
    accessorKey: 'unitTags',
    id: 'unitTags',
    header: 'Unit Tags',
  }),
];
